import { ReactNode, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { logout } from '@lootboy/lib/actions/AuthActions';
import { fetchLegalDataIfNeeded } from '@lootboy/lib/actions/LegalDataActions';
import { showLegalDataModalSelector } from '@lootboy/lib/selectors';
import { NOOP } from '@lootboy/lib/utils';

import { TermsConsentPopup } from '~/popups/TermsConsentPopup';
import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { isLegalDataUpdatedSelector } from '~/redux/selectors/legal';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import { MenuRouterTransition } from '~/routes/MenuRouter';
import { MenuDialogContainer } from '~/routes/MenuRouter/MenuDialogContainer';
import { EMAIL_REGISTER_PATH, REGISTER_TERMS_PATH } from '~/routes/paths';

const GDPRDialog = (): ReactNode => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const logged = useLoggedIn();

  useEffect(() => {
    dispatch(fetchLegalDataIfNeeded({ platform: 'web' })).catch(NOOP);
  }, [dispatch]);

  // this will be true when legal data was updated or when you use a new browser/device
  const showModal =
    useSelector((state: TAppStore) =>
      showLegalDataModalSelector(state, {
        requireAtLeastOneConsentToShow: true,
      }),
    ) && logged;

  // this will be true if there is a legal data version that has not been consented to by the user
  const wasLegalDataUpdated = useSelector(isLegalDataUpdatedSelector);

  const close = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  return (
    <MenuRouterTransition
      isIn={
        showModal &&
        // these are paths were users can be already logged in, but we don't want to show them the GDPR popup
        // because they will still be presented the registration terms popup
        ![REGISTER_TERMS_PATH, EMAIL_REGISTER_PATH].includes(pathname)
      }
    >
      <MenuDialogContainer onOutsideClick={close}>
        <TermsConsentPopup
          onClose={close}
          onApprove={NOOP}
          wasLegalDataUpdated={wasLegalDataUpdated}
        />
      </MenuDialogContainer>
    </MenuRouterTransition>
  );
};

export default GDPRDialog;
